import { useState, useEffect, useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useAuth } from '../../hooks/useAuth'
import { useCreatePlanItem } from '../../hooks/usePlanItems'
import { useSegments } from '../../hooks/useSegments'
import { useAidStations } from '../../hooks/useAidStations'
import { NutritionPreset } from '../../types'
import { PresetSelector } from './PresetSelector'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'

const planItemSchema = z.object({
  type: z.enum(['snack', 'drink', 'note']),
  name: z.string().min(1, 'Name is required').max(100, 'Name too long'),
  location_type: z.enum(['segment', 'aid_station']),
  location_id: z.string().min(1, 'Location is required'),
  carbs_g: z.number().min(0).optional(),
  fluids_ml: z.number().min(0).optional(),
  sodium_mg: z.number().min(0).optional(),
  caffeine_mg: z.number().min(0).optional(),
  notes: z.string().optional(),
})

type PlanItemForm = z.infer<typeof planItemSchema>

interface AddPlanItemDialogProps {
  runId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  preSelectedLocation?: {
    type: 'segment' | 'aid_station'
    id: string
    label: string
  }
}

export function AddPlanItemDialog({ runId, open, onOpenChange, preSelectedLocation }: AddPlanItemDialogProps) {
  const [error, setError] = useState<string | null>(null)
  const [selectedPreset, setSelectedPreset] = useState<NutritionPreset | null>(null)
  const { user } = useAuth()
  const createMutation = useCreatePlanItem()
  const { data: segments } = useSegments(runId)
  const { data: aidStations } = useAidStations(runId)
  
  const defaultValues = useMemo(() => ({
    type: 'snack' as const,
    location_type: preSelectedLocation?.type || 'segment' as const,
    location_id: preSelectedLocation?.id || '',
    name: '',
    carbs_g: undefined as number | undefined,
    fluids_ml: undefined as number | undefined,
    sodium_mg: undefined as number | undefined,
    caffeine_mg: undefined as number | undefined,
    notes: ''
  }), [preSelectedLocation])

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
    setValue
  } = useForm<PlanItemForm>({
    resolver: zodResolver(planItemSchema),
    defaultValues
  })

  const type = watch('type')
  const locationType = watch('location_type')

  // Reset location when location type changes (but not for preselected locations)
  useEffect(() => {
    if (!preSelectedLocation) {
      setValue('location_id', '')
    }
  }, [locationType, setValue, preSelectedLocation])

  // Handle preset selection
  const handlePresetSelect = useCallback((preset: NutritionPreset | null) => {
    setSelectedPreset(preset)
    if (preset) {
      setValue('name', preset.name)
      setValue('carbs_g', preset.carbs_g || undefined)
      setValue('fluids_ml', preset.fluids_ml || undefined) 
      setValue('sodium_mg', preset.sodium_mg || undefined)
      setValue('caffeine_mg', preset.caffeine_mg || undefined)
    }
  }, [setValue])

  const onSubmit = useCallback(async (data: PlanItemForm) => {
    setError(null)

    try {
      // Validate required fields based on type
      if (data.type === 'snack' && !data.carbs_g) {
        setError('Carbohydrates are required for snacks')
        return
      }
      if (data.type === 'drink' && !data.fluids_ml) {
        setError('Fluid volume is required for drinks')
        return
      }

      await createMutation.mutateAsync({
        run_id: runId,
        type: data.type,
        name: data.name.trim(),
        segment_id: data.location_type === 'segment' ? data.location_id : null,
        aid_station_id: data.location_type === 'aid_station' ? data.location_id : null,
        carbs_g: data.carbs_g || null,
        fluids_ml: data.fluids_ml || null,
        sodium_mg: data.sodium_mg || null,
        caffeine_mg: data.caffeine_mg || null,
        note: data.notes?.trim() || null,
      })

      reset(defaultValues)
      setSelectedPreset(null)
      onOpenChange(false)
    } catch (err) {
      console.error('Failed to create plan item:', err)
      setError(err instanceof Error ? err.message : 'Failed to create plan item')
    }
  }, [runId, createMutation, reset, defaultValues, onOpenChange])

  const handleOpenChange = useCallback((newOpen: boolean) => {
    if (!newOpen) {
      reset(defaultValues)
      setError(null)
      setSelectedPreset(null)
    }
    onOpenChange(newOpen)
  }, [onOpenChange, reset, defaultValues])

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {preSelectedLocation 
              ? `Add Item to ${preSelectedLocation.label}` 
              : 'Add Plan Item'
            }
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="type">Type</Label>
              <Select onValueChange={(value) => setValue('type', value as 'snack' | 'drink' | 'note')} defaultValue="snack">
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="snack">Snack</SelectItem>
                  <SelectItem value="drink">Drink</SelectItem>
                  <SelectItem value="note">Note</SelectItem>
                </SelectContent>
              </Select>
            </div>

{!preSelectedLocation && (
              <div className="space-y-2">
                <Label htmlFor="location_type">Location Type</Label>
                <Select onValueChange={(value) => setValue('location_type', value as 'segment' | 'aid_station')} defaultValue={defaultValues.location_type}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select location type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="segment">During Segment</SelectItem>
                    <SelectItem value="aid_station">At Aid Station</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {(type === 'snack' || type === 'drink') && user?.id && (
            <PresetSelector
              userId={user.id}
              selectedPreset={selectedPreset}
              onPresetSelect={handlePresetSelect}
            />
          )}

          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              {...register('name')}
              placeholder="e.g., Energy gel, Sports drink, Take salt"
            />
            {errors.name && (
              <p className="text-sm text-red-500">{errors.name.message}</p>
            )}
          </div>

{!preSelectedLocation && (
            <div className="space-y-2">
              <Label htmlFor="location_id">Location</Label>
              <Select onValueChange={(value) => setValue('location_id', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {locationType === 'segment' && segments?.map((segment, index) => (
                    <SelectItem key={segment.id} value={segment.id}>
                      {segment.label || `Segment ${index + 1}`} ({segment.distance_km}km)
                    </SelectItem>
                  ))}
                  {locationType === 'aid_station' && aidStations?.map((aidStation) => (
                    <SelectItem key={aidStation.id} value={aidStation.id}>
                      {aidStation.name} ({aidStation.at_distance_km}km)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.location_id && (
                <p className="text-sm text-red-500">{errors.location_id.message}</p>
              )}
            </div>
          )}

{type !== 'note' && (
            <div className="grid grid-cols-2 gap-4">
              {(type === 'snack' || type === 'drink') && (
                <div className="space-y-2">
                  <Label htmlFor="carbs_g">Carbohydrates (g)</Label>
                  <Input
                    id="carbs_g"
                    type="number"
                    step="0.1"
                    min="0"
                    {...register('carbs_g', { 
                      valueAsNumber: true,
                      setValueAs: (value) => value === '' || isNaN(value) ? undefined : value
                    })}
                    placeholder="25"
                  />
                </div>
              )}

              {type === 'drink' && (
                <div className="space-y-2">
                  <Label htmlFor="fluids_ml">Fluids (ml)</Label>
                  <Input
                    id="fluids_ml"
                    type="number"
                    min="0"
                    {...register('fluids_ml', { 
                      valueAsNumber: true,
                      setValueAs: (value) => value === '' || isNaN(value) ? undefined : value
                    })}
                    placeholder="500"
                  />
                </div>
              )}
            </div>
          )}

{type !== 'note' && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="sodium_mg">Sodium (mg)</Label>
                <Input
                  id="sodium_mg"
                  type="number"
                  min="0"
                  {...register('sodium_mg', { 
                    valueAsNumber: true,
                    setValueAs: (value) => value === '' || isNaN(value) ? undefined : value
                  })}
                  placeholder="200"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="caffeine_mg">Caffeine (mg)</Label>
                <Input
                  id="caffeine_mg"
                  type="number"
                  min="0"
                  {...register('caffeine_mg', { 
                    valueAsNumber: true,
                    setValueAs: (value) => value === '' || isNaN(value) ? undefined : value
                  })}
                  placeholder="50"
                />
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              {...register('notes')}
              placeholder="Any additional notes..."
              rows={3}
            />
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3" role="alert">
              {error}
            </div>
          )}

          <div className="flex justify-end space-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={createMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? 'Adding...' : 'Add Plan Item'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}